import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, Plus, Edit, Trash2, ArrowLeft, Upload, 
  LayoutDashboard, ShoppingBag, Mail, LogOut, Image, X,
  Save, Loader2, Flame
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, API } from '../App';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout, isAdmin, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      toast.error('Admin access required');
      navigate('/login');
    }
  }, [user, isAdmin, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/fragrances', icon: Flame, label: 'Fragrances' },
    { path: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
    { path: '/admin/messages', icon: Mail, label: 'Messages' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-[#2C2C2C] text-white p-6 fixed left-0 top-0">
          <div className="mb-8">
            <Link to="/" className="flex flex-col items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_cosy-commerce/artifacts/bln0ehzh_ChatGPT_Image_Feb_8__2026__08_25_25_PM-removebg-preview.png" 
                alt="Glow & Gather Candles" 
                className="h-16 w-auto"
              />
              <p className="text-xs text-stone-400">Admin Panel</p>
            </Link>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#C5A059] text-white'
                    : 'text-stone-400 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="border-t border-stone-700 pt-6 mb-4">
              <p className="text-sm text-stone-400 mb-1">Logged in as</p>
              <p className="font-medium truncate">{user?.name}</p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-stone-400 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
            <Link to="/">
              <Button
                variant="ghost"
                className="w-full justify-start text-stone-400 hover:text-white hover:bg-white/10 mt-2"
              >
                <ArrowLeft className="w-5 h-5 mr-3" />
                Back to Site
              </Button>
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 p-8">
          <Routes>
            <Route path="/" element={<DashboardOverview token={token} />} />
            <Route path="/products" element={<ProductsManagement token={token} />} />
            <Route path="/fragrances" element={<FragrancesManagement token={token} />} />
            <Route path="/orders" element={<OrdersManagement token={token} />} />
            <Route path="/messages" element={<MessagesManagement token={token} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = ({ token }) => {
  const [stats, setStats] = useState({ products: 0, orders: 0, messages: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const productsRes = await fetch(`${API}/products`);
        if (productsRes.ok) {
          const products = await productsRes.json();
          setStats(prev => ({ ...prev, products: products.length }));
        }

        const ordersRes = await fetch(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (ordersRes.ok) {
          const orders = await ordersRes.json();
          setStats(prev => ({ ...prev, orders: orders.length }));
        }

        const messagesRes = await fetch(`${API}/contact/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (messagesRes.ok) {
          const messages = await messagesRes.json();
          setStats(prev => ({ ...prev, messages: messages.length }));
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, [token]);

  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2C2C2C] mb-8">Dashboard</h1>
      
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-stone-100"
        >
          <Package className="w-8 h-8 text-[#C5A059] mb-4" />
          <p className="text-3xl font-bold text-[#2C2C2C]">{stats.products}</p>
          <p className="text-[#8C8C8C]">Products</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-stone-100"
        >
          <ShoppingBag className="w-8 h-8 text-[#5D6D53] mb-4" />
          <p className="text-3xl font-bold text-[#2C2C2C]">{stats.orders}</p>
          <p className="text-[#8C8C8C]">Orders</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-stone-100"
        >
          <Mail className="w-8 h-8 text-[#D48C70] mb-4" />
          <p className="text-3xl font-bold text-[#2C2C2C]">{stats.messages}</p>
          <p className="text-[#8C8C8C]">Messages</p>
        </motion.div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-stone-100">
        <h2 className="font-serif text-xl text-[#2C2C2C] mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/admin/products">
            <Button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add New Product
            </Button>
          </Link>
          <Link to="/admin/orders">
            <Button variant="outline" className="btn-secondary">
              View Orders
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Products Management
const ProductsManagement = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const categories = [
    { id: 'candles', name: 'Candles' },
    { id: 'snap-bars', name: 'Snap Bars' },
    { id: 'wax-melts', name: 'Wax Melts' },
    { id: 'wax-gels', name: 'Wax Gels' },
    { id: 'wax-burners', name: 'Wax Burners' },
    { id: 'bundles', name: 'Bundles' },
  ];

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (productId) => {
    try {
      const response = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
    setDeleteId(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif text-3xl text-[#2C2C2C]">Products</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="btn-primary" 
              onClick={() => setEditingProduct(null)}
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>
            <ProductForm
              product={editingProduct}
              categories={categories}
              token={token}
              onSuccess={() => {
                setIsDialogOpen(false);
                fetchProducts();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
        </div>
      ) : products.length > 0 ? (
        <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left p-4 font-medium text-[#595959]">Product</th>
                <th className="text-left p-4 font-medium text-[#595959]">Category</th>
                <th className="text-left p-4 font-medium text-[#595959]">Price</th>
                <th className="text-left p-4 font-medium text-[#595959]">Stock</th>
                <th className="text-left p-4 font-medium text-[#595959]">Featured</th>
                <th className="text-right p-4 font-medium text-[#595959]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-t border-stone-100" data-testid={`product-row-${product.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F5F1EB] flex-shrink-0">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-5 h-5 text-[#8C8C8C]" />
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-[#2C2C2C]">{product.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-[#595959] capitalize">{product.category?.replace('-', ' ')}</td>
                  <td className="p-4 text-[#C5A059] font-medium">£{product.price?.toFixed(2)}</td>
                  <td className="p-4 text-[#595959]">{product.stock}</td>
                  <td className="p-4">
                    {product.featured && (
                      <span className="px-2 py-1 bg-[#5D6D53] text-white text-xs rounded-full">Featured</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingProduct(product);
                          setIsDialogOpen(true);
                        }}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setDeleteId(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-100">
          <Package className="w-12 h-12 text-[#8C8C8C] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">No Products Yet</h3>
          <p className="text-[#8C8C8C] mb-4">Add your first product to get started</p>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The product will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => handleDelete(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Product Form
const ProductForm = ({ product, categories, token, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fragrances, setFragrances] = useState([]);
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || '',
    image_url: product?.image_url || '',
    stock: product?.stock || 0,
    scent: product?.scent || '',
    burn_time: product?.burn_time || '',
    weight: product?.weight || '',
    featured: product?.featured || false,
    fragrance_type: product?.fragrance_type || 'none',
    fixed_fragrance: product?.fixed_fragrance || '',
  });

  // Fetch fragrances for dropdown
  useEffect(() => {
    const fetchFragrances = async () => {
      try {
        const response = await fetch(`${API}/fragrances/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setFragrances(data.filter(f => f.active));
        }
      } catch (error) {
        console.error('Failed to fetch fragrances:', error);
      }
    };
    fetchFragrances();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const response = await fetch(`${API}/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataObj
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: data.image_url }));
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const url = product ? `${API}/products/${product.id}` : `${API}/products`;
      const method = product ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          stock: parseInt(formData.stock),
          fixed_fragrance: formData.fragrance_type === 'fixed' ? formData.fixed_fragrance : null
        })
      });

      if (response.ok) {
        toast.success(product ? 'Product updated' : 'Product created');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Failed to save product');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Image Upload */}
      <div>
        <Label>Product Image</Label>
        <div className="mt-2 flex items-start gap-4">
          <div className="w-32 h-32 rounded-lg overflow-hidden bg-[#F5F1EB] flex-shrink-0 border border-stone-200">
            {formData.image_url ? (
              <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-8 h-8 text-[#8C8C8C]" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              data-testid="image-upload"
            />
            <label htmlFor="image-upload">
              <Button type="button" variant="outline" className="cursor-pointer" asChild disabled={uploading}>
                <span>
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Image
                </span>
              </Button>
            </label>
            <p className="text-xs text-[#8C8C8C] mt-2">PNG, JPG up to 5MB</p>
            {formData.image_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 text-red-500"
                onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
              >
                <X className="w-4 h-4 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="input-glow mt-1"
            data-testid="product-name-input"
          />
        </div>
        <div>
          <Label htmlFor="category">Category *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
          >
            <SelectTrigger className="mt-1" data-testid="product-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          className="input-glow mt-1 min-h-[100px]"
          data-testid="product-description-input"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="price">Price (£) *</Label>
          <Input
            id="price"
            name="price"
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={handleInputChange}
            required
            className="input-glow mt-1"
            data-testid="product-price-input"
          />
        </div>
        <div>
          <Label htmlFor="stock">Stock</Label>
          <Input
            id="stock"
            name="stock"
            type="number"
            min="0"
            value={formData.stock}
            onChange={handleInputChange}
            className="input-glow mt-1"
            data-testid="product-stock-input"
          />
        </div>
        <div>
          <Label htmlFor="scent">Scent</Label>
          <Input
            id="scent"
            name="scent"
            value={formData.scent}
            onChange={handleInputChange}
            className="input-glow mt-1"
            placeholder="e.g. Vanilla & Sandalwood"
            data-testid="product-scent-input"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="burn_time">Burn Time</Label>
          <Input
            id="burn_time"
            name="burn_time"
            value={formData.burn_time}
            onChange={handleInputChange}
            className="input-glow mt-1"
            placeholder="e.g. 40-50 hours"
          />
        </div>
        <div>
          <Label htmlFor="weight">Weight</Label>
          <Input
            id="weight"
            name="weight"
            value={formData.weight}
            onChange={handleInputChange}
            className="input-glow mt-1"
            placeholder="e.g. 220g"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="featured"
          checked={formData.featured}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
          data-testid="product-featured-switch"
        />
        <Label htmlFor="featured">Featured Product</Label>
      </div>

      {/* Fragrance Options */}
      <div className="border-t border-stone-200 pt-6 mt-2">
        <Label className="text-base font-semibold text-[#2C2C2C] mb-4 block">Fragrance Options</Label>
        <p className="text-sm text-[#8C8C8C] mb-4">Choose how customers can select fragrances for this product</p>
        
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:border-[#C5A059] transition-colors">
            <input
              type="radio"
              name="fragrance_type"
              value="none"
              checked={formData.fragrance_type === 'none'}
              onChange={(e) => setFormData(prev => ({ ...prev, fragrance_type: e.target.value, fixed_fragrance: '' }))}
              className="w-4 h-4 text-[#C5A059] focus:ring-[#C5A059]"
              data-testid="fragrance-type-none"
            />
            <div>
              <span className="font-medium text-[#2C2C2C]">No Fragrance Selection</span>
              <p className="text-xs text-[#8C8C8C]">Product has no fragrance options</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:border-[#C5A059] transition-colors">
            <input
              type="radio"
              name="fragrance_type"
              value="fixed"
              checked={formData.fragrance_type === 'fixed'}
              onChange={(e) => setFormData(prev => ({ ...prev, fragrance_type: e.target.value }))}
              className="w-4 h-4 text-[#C5A059] focus:ring-[#C5A059]"
              data-testid="fragrance-type-fixed"
            />
            <div>
              <span className="font-medium text-[#2C2C2C]">Single Fixed Fragrance</span>
              <p className="text-xs text-[#8C8C8C]">Product comes in one specific fragrance only</p>
            </div>
          </label>
          
          {formData.fragrance_type === 'fixed' && (
            <div className="ml-7 mt-2">
              <Input
                type="text"
                placeholder="Enter the fragrance name (e.g., Scottish Heather)"
                value={formData.fixed_fragrance}
                onChange={(e) => setFormData(prev => ({ ...prev, fixed_fragrance: e.target.value }))}
                className="input-glow"
                data-testid="fixed-fragrance-input"
              />
            </div>
          )}
          
          <label className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:border-[#C5A059] transition-colors">
            <input
              type="radio"
              name="fragrance_type"
              value="selectable"
              checked={formData.fragrance_type === 'selectable'}
              onChange={(e) => setFormData(prev => ({ ...prev, fragrance_type: e.target.value, fixed_fragrance: '' }))}
              className="w-4 h-4 text-[#C5A059] focus:ring-[#C5A059]"
              data-testid="fragrance-type-selectable"
            />
            <div>
              <span className="font-medium text-[#2C2C2C]">Customer Selectable</span>
              <p className="text-xs text-[#8C8C8C]">Customer can choose from the fragrance list you manage</p>
            </div>
          </label>
          
          {formData.fragrance_type === 'selectable' && fragrances.length === 0 && (
            <div className="ml-7 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <Flame className="w-4 h-4 inline mr-1" />
                No active fragrances found. Add fragrances in the <Link to="/admin/fragrances" className="underline font-medium">Fragrances</Link> section first.
              </p>
            </div>
          )}
          
          {formData.fragrance_type === 'selectable' && fragrances.length > 0 && (
            <div className="ml-7 mt-2 p-3 bg-[#F5F1EB] border border-stone-200 rounded-lg">
              <p className="text-sm text-[#595959] mb-2">
                <Flame className="w-4 h-4 inline mr-1 text-[#C5A059]" />
                Customers will choose from {fragrances.length} active fragrance{fragrances.length > 1 ? 's' : ''}:
              </p>
              <div className="flex flex-wrap gap-1">
                {fragrances.slice(0, 5).map(f => (
                  <span key={f.id} className="text-xs bg-white px-2 py-1 rounded-full text-[#2C2C2C]">{f.name}</span>
                ))}
                {fragrances.length > 5 && (
                  <span className="text-xs bg-white px-2 py-1 rounded-full text-[#8C8C8C]">+{fragrances.length - 5} more</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="save-product-btn">
        {loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Save className="w-4 h-4 mr-2" />
        )}
        {product ? 'Update Product' : 'Create Product'}
      </Button>
    </form>
  );
};

// Orders Management
const OrdersManagement = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Sort by newest first
        setOrders(data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      const response = await fetch(`${API}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Order marked as ${newStatus}`);
        fetchOrders();
      } else {
        toast.error('Failed to update order status');
      }
    } catch (error) {
      toast.error('Failed to update order status');
    }
    setUpdatingStatus(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-700';
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      case 'shipped':
        return 'bg-[#5D6D53] text-white';
      case 'delivered':
        return 'bg-green-600 text-white';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-stone-100 text-[#595959]';
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'shipped' || status === 'delivered') {
      return <span className="mr-1">✓</span>;
    }
    return null;
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2C2C2C] mb-8">Orders</h1>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
        </div>
      ) : orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-stone-100 overflow-hidden">
              {/* Order Header - Always visible */}
              <div 
                className="p-6 cursor-pointer hover:bg-stone-50 transition-colors"
                onClick={() => toggleOrderDetails(order.id)}
                data-testid={`order-${order.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-lg text-[#2C2C2C]">{order.customer_name || 'Guest'}</p>
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        {order.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#8C8C8C]">{order.customer_email}</p>
                    <p className="text-xs text-[#8C8C8C] mt-1">
                      {new Date(order.created_at).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#C5A059]">£{order.total?.toFixed(2)}</p>
                    <p className="text-sm text-[#8C8C8C]">{order.items?.length} item(s)</p>
                  </div>
                </div>
              </div>

              {/* Expanded Order Details */}
              {expandedOrder === order.id && (
                <div className="border-t border-stone-100 p-6 bg-[#FAF9F6]">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Items Ordered */}
                    <div>
                      <h4 className="font-semibold text-[#2C2C2C] mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#C5A059]" />
                        Items Ordered
                      </h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-medium text-[#2C2C2C]">{item.name}</p>
                              {item.fragrance && (
                                <p className="text-sm text-[#C5A059] flex items-center gap-1">
                                  <Flame className="w-3 h-3" />
                                  {item.fragrance}
                                </p>
                              )}
                              <p className="text-sm text-[#8C8C8C]">Qty: {item.quantity}</p>
                            </div>
                            <p className="font-semibold text-[#C5A059]">£{item.subtotal?.toFixed(2)}</p>
                          </div>
                        ))}
                        {order.shipping && (
                          <div className="bg-white p-3 rounded-lg flex justify-between items-center">
                            <p className="text-[#8C8C8C]">Postage & Packing</p>
                            <p className="text-[#595959]">£{order.shipping?.toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-semibold text-[#2C2C2C] mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#C5A059]" />
                        Shipping Address
                      </h4>
                      <div className="bg-white p-4 rounded-lg">
                        <p className="font-medium text-[#2C2C2C] mb-2">{order.customer_name}</p>
                        {order.shipping_address ? (
                          <>
                            {order.shipping_address.address && (
                              <p className="text-[#595959]">{order.shipping_address.address}</p>
                            )}
                            <p className="text-[#595959]">
                              {[order.shipping_address.city, order.shipping_address.postcode].filter(Boolean).join(', ')}
                            </p>
                            {order.shipping_address.country && (
                              <p className="text-[#595959]">{order.shipping_address.country}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-[#8C8C8C] italic">No shipping address provided</p>
                        )}
                        <div className="mt-3 pt-3 border-t border-stone-100">
                          <p className="text-sm text-[#8C8C8C]">Email: {order.customer_email}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes to Seller */}
                  {order.notes_to_seller && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-[#2C2C2C] mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#C5A059]" />
                        Notes from Customer
                      </h4>
                      <div className="bg-[#FFF8E8] p-4 rounded-lg border-l-4 border-[#C5A059]">
                        <p className="text-[#595959] whitespace-pre-wrap">{order.notes_to_seller}</p>
                      </div>
                    </div>
                  )}

                  {/* Order Actions */}
                  <div className="mt-6 pt-4 border-t border-stone-200">
                    <h4 className="font-semibold text-[#2C2C2C] mb-3">Update Status</h4>
                    <div className="flex flex-wrap gap-2">
                      {order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateOrderStatus(order.id, 'shipped');
                          }}
                          disabled={updatingStatus === order.id}
                          className="bg-[#5D6D53] hover:bg-[#4A5842] text-white"
                          data-testid={`mark-shipped-${order.id}`}
                        >
                          {updatingStatus === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <span className="mr-2">✓</span>
                          )}
                          Mark as Shipped
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateOrderStatus(order.id, 'delivered');
                          }}
                          disabled={updatingStatus === order.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`mark-delivered-${order.id}`}
                        >
                          {updatingStatus === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <span className="mr-2">✓</span>
                          )}
                          Mark as Delivered
                        </Button>
                      )}
                      {order.status !== 'cancelled' && order.status !== 'delivered' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateOrderStatus(order.id, 'cancelled');
                          }}
                          disabled={updatingStatus === order.id}
                          className="text-red-500 border-red-200 hover:bg-red-50"
                          data-testid={`cancel-order-${order.id}`}
                        >
                          Cancel Order
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Order ID */}
                  <div className="mt-4 pt-4 border-t border-stone-200 text-xs text-[#8C8C8C]">
                    Order ID: {order.id}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-100">
          <ShoppingBag className="w-12 h-12 text-[#8C8C8C] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">No Orders Yet</h3>
          <p className="text-[#8C8C8C]">Orders will appear here once customers start purchasing</p>
        </div>
      )}
    </div>
  );
};

// Fragrances Management
const FragrancesManagement = ({ token }) => {
  const [fragrances, setFragrances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFragrance, setNewFragrance] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchFragrances = async () => {
    try {
      const response = await fetch(`${API}/fragrances/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFragrances(data);
      }
    } catch (error) {
      console.error('Failed to fetch fragrances:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFragrances();
  }, [token]);

  const addFragrance = async () => {
    if (!newFragrance.trim()) {
      toast.error('Please enter a fragrance name');
      return;
    }

    setAdding(true);
    try {
      const response = await fetch(`${API}/fragrances`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newFragrance.trim() })
      });

      if (response.ok) {
        toast.success('Fragrance added');
        setNewFragrance('');
        fetchFragrances();
      } else {
        toast.error('Failed to add fragrance');
      }
    } catch (error) {
      toast.error('Failed to add fragrance');
    }
    setAdding(false);
  };

  const toggleFragrance = async (fragrance) => {
    try {
      const response = await fetch(`${API}/fragrances/${fragrance.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ active: !fragrance.active })
      });

      if (response.ok) {
        toast.success(fragrance.active ? 'Fragrance disabled' : 'Fragrance enabled');
        fetchFragrances();
      }
    } catch (error) {
      toast.error('Failed to update fragrance');
    }
  };

  const deleteFragrance = async (fragranceId) => {
    try {
      const response = await fetch(`${API}/fragrances/${fragranceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Fragrance deleted');
        fetchFragrances();
      } else {
        toast.error('Failed to delete fragrance');
      }
    } catch (error) {
      toast.error('Failed to delete fragrance');
    }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2C2C2C] mb-8">Fragrances</h1>

      {/* Add New Fragrance */}
      <div className="bg-white rounded-xl p-6 border border-stone-100 mb-8">
        <h2 className="font-semibold text-[#2C2C2C] mb-4">Add New Fragrance</h2>
        <div className="flex gap-4">
          <Input
            type="text"
            placeholder="Enter fragrance name (e.g., Vanilla & Sandalwood)"
            value={newFragrance}
            onChange={(e) => setNewFragrance(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addFragrance()}
            className="input-glow flex-1"
            data-testid="new-fragrance-input"
          />
          <Button
            onClick={addFragrance}
            disabled={adding}
            className="btn-primary"
            data-testid="add-fragrance-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add
          </Button>
        </div>
      </div>

      {/* Fragrances List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
        </div>
      ) : fragrances.length > 0 ? (
        <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
          <div className="p-4 bg-stone-50 border-b border-stone-100">
            <p className="text-sm text-[#8C8C8C]">{fragrances.filter(f => f.active).length} active fragrances</p>
          </div>
          <div className="divide-y divide-stone-100">
            {fragrances.map((fragrance) => (
              <div
                key={fragrance.id}
                className={`p-4 flex items-center justify-between ${!fragrance.active ? 'opacity-50 bg-stone-50' : ''}`}
                data-testid={`fragrance-${fragrance.id}`}
              >
                <div className="flex items-center gap-3">
                  <Flame className={`w-5 h-5 ${fragrance.active ? 'text-[#C5A059]' : 'text-[#8C8C8C]'}`} />
                  <span className={`font-medium ${fragrance.active ? 'text-[#2C2C2C]' : 'text-[#8C8C8C]'}`}>
                    {fragrance.name}
                  </span>
                  {!fragrance.active && (
                    <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded">Disabled</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleFragrance(fragrance)}
                    className={fragrance.active ? 'text-[#8C8C8C]' : 'text-[#5D6D53]'}
                  >
                    {fragrance.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteFragrance(fragrance.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-100">
          <Flame className="w-12 h-12 text-[#8C8C8C] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">No Fragrances Yet</h3>
          <p className="text-[#8C8C8C]">Add your first fragrance above</p>
        </div>
      )}
    </div>
  );
};

// Messages Management
const MessagesManagement = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await fetch(`${API}/contact/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMessages(data);
        }
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      }
      setLoading(false);
    };
    fetchMessages();
  }, [token]);

  return (
    <div>
      <h1 className="font-serif text-3xl text-[#2C2C2C] mb-8">Contact Messages</h1>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
        </div>
      ) : messages.length > 0 ? (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className="bg-white rounded-xl p-6 border border-stone-100">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="font-medium text-[#2C2C2C]">{msg.name}</p>
                  <p className="text-sm text-[#8C8C8C]">{msg.email}</p>
                </div>
                <p className="text-xs text-[#8C8C8C]">
                  {new Date(msg.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <p className="font-medium text-[#2C2C2C] mb-2">{msg.subject}</p>
              <p className="text-[#595959] text-sm">{msg.message}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-100">
          <Mail className="w-12 h-12 text-[#8C8C8C] mx-auto mb-4" />
          <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">No Messages Yet</h3>
          <p className="text-[#8C8C8C]">Contact form submissions will appear here</p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
